'use client';
import React, { useState, useId } from 'react';
import { motion } from 'motion/react';
import { type LinkField } from 'lib/types';
import { cn } from 'lib/utils';
import {
  type BloomProps,
  Bloom,
  HeaderSection,
  type HeaderSectionProps,
  Wrapper,
} from 'ui';
import ResourceGridFull from './ResourceGridFull';
import ResourceGridMobile from './ResourceGridMobile';

export type ResourceGridSection = {
  title: string;
  items: {
    eyebrow: string;
    title: string;
    link: LinkField;
    publication?: string;
    publicationYear?: number;
  }[];
  itemsLayout: 'blogs' | 'news';
  columns?: 2 | 3;
  footerLinks?: LinkField[];
};

export type ResourceGridProps = {
  className?: string;
  eyebrow?: string;
  title?: string;
  intro?: string;
  headerAlignment?: HeaderSectionProps['alignment'];
  sections: ResourceGridSection[];
  bloom?: BloomProps;
};

export type ResourceGridChildProps = ResourceGridProps & {
  activeSection: number;
  showToggle: boolean;
  toggleId: string;
};

const ResourceGrid = ({
  bloom,
  className,
  eyebrow,
  title,
  intro,
  headerAlignment,
  ...props
}: ResourceGridProps) => {
  const [activeSection, setActiveSection] = useState(0);
  const toggleId = useId();

  if (!props.sections || props.sections.length === 0) {
    return null;
  }

  // Only show first 2 sections if more than 2 exist
  const sectionsToShow = props.sections.slice(0, 2);
  const showToggle = sectionsToShow.length > 1;

  return (
    <Wrapper
      as="section"
      className={cn('relative flex flex-col gap-11 lg:gap-12', className)}
    >
      <Bloom {...bloom} />

      <HeaderSection
        content={intro}
        alignment={headerAlignment}
        {...{ eyebrow, title }}
        className="relative z-2"
      />

      {/* Pill-shaped toggle switch */}
      {showToggle && (
        <div className="relative z-2 flex justify-center">
          <div className="relative bg-gradient-to-b from-graphite-400/50 to-graphite-400/75 p-1 rounded-full grid grid-cols-2">
            {sectionsToShow.map((section, index) => (
              <button
                key={index}
                className={cn(
                  'max-lg:text-p3/[1.25rem] lg:text-p1/[1.25rem] relative z-10 flex-1 px-4 lg:px-6 py-3 text-sm font-medium rounded-full transition-colors duration-300 no-underline text-center cursor-pointer',
                  {
                    'text-graphite-900 text-stroke-graphite-900':
                      activeSection === index,
                    'text-clay-50 [&:hover,&:focus]:text-stroke-clay-50':
                      activeSection !== index,
                  }
                )}
                onClick={() => setActiveSection(index)}
                role="tab"
                aria-selected={activeSection === index}
                aria-controls={`${toggleId}-panel-${index}`}
                id={`${toggleId}-tab-${index}`}
              >
                {section.title}
              </button>
            ))}

            {/* Animated pill background */}
            <motion.div
              className="absolute top-1 bottom-1 left-1 bg-clay-50 rounded-full"
              style={{ width: 'calc(50% - 0.375rem)' }}
              initial={false}
              animate={{
                x: activeSection === 0 ? 0 : 'calc(100% + 0.25rem)',
              }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
              }}
              aria-hidden="true"
            />
          </div>
        </div>
      )}

      <ResourceGridMobile
        {...props}
        sections={sectionsToShow}
        activeSection={activeSection}
        showToggle={showToggle}
        toggleId={toggleId}
      />

      <ResourceGridFull
        {...props}
        sections={sectionsToShow}
        activeSection={activeSection}
        showToggle={showToggle}
        toggleId={toggleId}
      />
    </Wrapper>
  );
};

export default ResourceGrid;
